import { useState } from 'react';
import {
  BriefcaseBusiness,
  BarChart3,
  ChevronDown,
  Factory,
  Home,
  ListChecks,
  PackageCheck,
  Truck,
  WalletCards,
} from 'lucide-react';

const navigationItems = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'comercial', label: 'Comercial', icon: BriefcaseBusiness },
  { id: 'faturamento', label: 'Faturamento', icon: BarChart3 },
  { id: 'financeiro', label: 'Financeiro', icon: WalletCards },
  { id: 'producao', label: 'Produção', icon: Factory },
  { id: 'expedicao', label: 'Expedição', icon: PackageCheck },
  { id: 'logistica', label: 'Logística', icon: Truck },
];

const financialSubItems = [
  { id: 'financeiro-pagamentos', label: 'Lista Pagamento' },
  { id: 'financeiro-recebimentos', label: 'Lista Recebimento' },
];

const billingSubItems = [{ id: 'faturamento-painel', label: 'Painel de Faturamento' }];
const shippingSubItems = [{ id: 'expedicao-painel', label: 'Painel de Expedição' }];
const productionSubItems = [{ id: 'producao-painel', label: 'Painel de Produção' }];
const logisticsSubItems = [{ id: 'logistica-pde', label: 'PDE - Programação de Entrega' }];

export default function Sidebar({ activeSection, onNavigate, disabledSections = [] }) {
  const [expandedMenus, setExpandedMenus] = useState({});

  return (
    <aside className="w-full shrink-0 border-b border-gray-800 bg-[#10161d] lg:min-h-[calc(100vh-4rem)] lg:w-60 lg:border-r lg:border-b-0">
      <div className="flex h-full flex-col p-3 lg:p-4">
        <div className="mb-3 hidden border-b border-gray-800 pb-4 lg:block">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.32em] text-gray-500">
            Navegação
          </p>
        </div>

        <nav aria-label="Áreas do dashboard">
          <ul className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible">
            {navigationItems.map(({ id, label, icon: Icon }) => {
              const subItems = id === 'financeiro' ? financialSubItems : id === 'faturamento' ? billingSubItems : id === 'expedicao' ? shippingSubItems : id === 'producao' ? productionSubItems : id === 'logistica' ? logisticsSubItems : [];
              const hasSubItems = subItems.length > 0;
              const isActive = activeSection === id || (hasSubItems && activeSection.startsWith(`${id}-`));
              const isDisabled = disabledSections.includes(id);
              const shouldShowSubItems = hasSubItems && (expandedMenus[id] ?? isActive);

              return (
              <li key={label} className="shrink-0 lg:w-full">
                <button
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  aria-expanded={hasSubItems ? shouldShowSubItems : undefined}
                  aria-controls={hasSubItems ? `${id}-submenu` : undefined}
                  aria-disabled={isDisabled}
                  disabled={isDisabled}
                  onClick={() => {
                    if (hasSubItems) {
                      setExpandedMenus(current => ({ ...current, [id]: !shouldShowSubItems }));
                    }
                    onNavigate(id);
                  }}
                  className={`flex min-h-11 w-full items-center gap-3 border px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.14em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-400 lg:min-w-0 ${
                    isActive
                      ? 'border-green-500/50 bg-green-500/10 text-green-400 shadow-[inset_3px_0_0_0_#22c55e]'
                      : isDisabled
                        ? 'cursor-not-allowed border-transparent text-gray-700'
                        : 'border-transparent text-gray-400 hover:border-gray-700 hover:bg-gray-900 hover:text-gray-200'
                  }`}
                >
                  <Icon aria-hidden="true" size={17} strokeWidth={1.8} />
                  <span>{label}</span>
                  {hasSubItems && (
                    <ChevronDown
                      aria-hidden="true"
                      size={15}
                      className={`ml-auto transition-transform ${shouldShowSubItems ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>
                {shouldShowSubItems && !isDisabled && (
                  <ul id={`${id}-submenu`} className="mt-1 space-y-1 border-l border-gray-800 pl-3 lg:ml-3">
                    {subItems.map((subItem) => {
                      const isSubItemActive = activeSection === subItem.id;

                      return (
                        <li key={subItem.id}>
                          <button
                            type="button"
                            disabled={disabledSections.includes(subItem.id)}
                            aria-disabled={disabledSections.includes(subItem.id)}
                            aria-current={isSubItemActive ? 'page' : undefined}
                            onClick={() => {
                              setExpandedMenus(current => ({ ...current, [id]: true }));
                              onNavigate(subItem.id);
                            }}
                            className={`flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-[9px] font-bold uppercase tracking-[0.12em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-400 ${
                              isSubItemActive
                                ? 'bg-green-500/10 text-green-400'
                                : 'text-gray-500 hover:bg-gray-900 hover:text-gray-200'
                            }`}
                          >
                            <ListChecks aria-hidden="true" size={14} strokeWidth={1.8} />
                            <span>{subItem.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
